// PATH: src/pages/CatalogPage.tsx
/**
 * Catalogue : « comment est structuré le référentiel ? ». Maître-détail :
 * l'arbre Programme → Étape → Niveau à gauche, la fiche du nœud à droite
 * (étapes et matières d'un programme, niveaux d'une étape, filières et
 * matières offertes d'un niveau). Une seule lecture : /v1/curriculum/overview.
 */
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/AddRounded";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import AccountTreeIcon from "@mui/icons-material/AccountTreeOutlined";
import UnfoldIcon from "@mui/icons-material/UnfoldMoreRounded";
import { EmptyState, PageHeader } from "../components/ui";
import { tokens } from "../theme/theme";
import { CatalogProvider } from "../components/catalog/CatalogContext";
import { CatalogTree } from "../components/catalog/CatalogTree";
import { GradePanel } from "../components/catalog/GradePanel";
import { ProgramPanel } from "../components/catalog/ProgramPanel";
import { StagePanel } from "../components/catalog/StagePanel";
import { ProgramDialog } from "../components/catalog/forms/ProgramDialog";
import { useCatalogData } from "../components/catalog/useCatalogData";
import {
  formatNodeParam,
  nodePath,
  parseNodeParam,
  resolveNode,
  sameNode,
  type CatalogIndex,
  type NodeRef,
} from "../components/catalog/catalogModel";

const TREE_WIDTH = 300;

/** Libellé court du chemin (sélecteur mobile) : « Marocain › Lycée › L1 ». */
const pathLabel = (index: CatalogIndex, node: NodeRef | null): string =>
  nodePath(index, node)
    .map((n) =>
      n.kind === "program"
        ? index.programById.get(n.id)?.program.name
        : n.kind === "stage"
          ? index.stageById.get(n.id)?.stage.stage.name
          : index.gradeById.get(n.id)?.grade.grade.code,
    )
    .filter(Boolean)
    .join(" › ");

const LoadingLayout: React.FC<{ mobile: boolean }> = ({ mobile }) => (
  <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: mobile ? "1fr" : `${TREE_WIDTH}px minmax(0, 1fr)` }}>
    {!mobile && (
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
        <Skeleton variant="rounded" height={36} sx={{ mb: 1.5 }} />
        {[90, 70, 60, 60, 70, 90, 70].map((w, i) => (
          <Skeleton key={i} height={30} width={`${w}%`} />
        ))}
      </Paper>
    )}
    <Stack spacing={2.5}>
      <Skeleton height={56} width="50%" />
      <Skeleton variant="rounded" height={120} />
      <Skeleton variant="rounded" height={260} />
    </Stack>
  </Box>
);

const CatalogPage: React.FC = () => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("md"));
  const { index, loading, refreshing, error, reload, retry } = useCatalogData();
  const [params, setParams] = useSearchParams();
  const [treeOpen, setTreeOpen] = React.useState(false);
  const [creating, setCreating] = React.useState(false);

  const wanted = React.useMemo(() => parseNodeParam(params.get("node")), [params]);
  // Dernier nœud affiché : si on le supprime, on retombe sur son parent.
  const lastPath = React.useRef<NodeRef[]>([]);
  const node = React.useMemo(() => {
    if (loading) return wanted;
    const parents = lastPath.current;
    return resolveNode(index, wanted, parents[parents.length - 2] ?? parents[0]);
  }, [index, wanted, loading]);

  React.useEffect(() => {
    if (!loading && node) lastPath.current = nodePath(index, node);
  }, [index, node, loading]);

  // Lien absent ou périmé : on réécrit l'URL sans ajouter d'entrée d'historique.
  React.useEffect(() => {
    if (loading || !node || sameNode(node, wanted)) return;
    setParams({ node: formatNodeParam(node) }, { replace: true });
  }, [loading, node, wanted, setParams]);

  const select = React.useCallback(
    (n: NodeRef) => {
      setTreeOpen(false);
      if (sameNode(n, wanted)) return;
      setParams({ node: formatNodeParam(n) });
    },
    [setParams, wanted],
  );

  const ctx = React.useMemo(() => ({ index, reload, select }), [index, reload, select]);

  const tree = (
    <CatalogTree programs={index.programs} index={index} selected={node} onSelect={select} />
  );

  let detail: React.ReactNode = null;
  if (node?.kind === "program") {
    const p = index.programById.get(node.id);
    if (p) detail = <ProgramPanel key={node.id} node={p} />;
  } else if (node?.kind === "stage") {
    const s = index.stageById.get(node.id);
    if (s) detail = <StagePanel key={node.id} entry={s} />;
  } else if (node?.kind === "grade") {
    const g = index.gradeById.get(node.id);
    if (g) detail = <GradePanel key={node.id} entry={g} />;
  }

  return (
    <CatalogProvider value={ctx}>
      <Box>
        <PageHeader
          title="Catalogue"
          subtitle="La structure du référentiel : programmes, étapes, niveaux, filières, matières et ce que chaque niveau propose."
          actions={
            <>
              <Tooltip title="Relire le catalogue">
                <span>
                  <IconButton onClick={() => void reload()} disabled={loading || refreshing} aria-label="Relire le catalogue">
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} disabled={loading}>
                Nouveau programme
              </Button>
            </>
          }
        />

        <Box sx={{ height: 4, mt: -2, mb: 2 }}>{refreshing && <LinearProgress sx={{ height: 3 }} />}</Box>

        {loading ? (
          <LoadingLayout mobile={mobile} />
        ) : error && index.programs.length === 0 ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void retry()}>
                Réessayer
              </Button>
            }
          >
            {error}
          </Alert>
        ) : index.programs.length === 0 ? (
          <Paper variant="outlined" sx={{ borderRadius: 3 }}>
            <EmptyState
              icon={<AccountTreeIcon />}
              title="Aucun programme"
              description="Créez un programme (ex. Programme Marocain), puis ses étapes, ses niveaux et ses matières."
              action={
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
                  Nouveau programme
                </Button>
              }
            />
          </Paper>
        ) : (
          <>
            {error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {mobile ? (
              <>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setTreeOpen(true)}
                  startIcon={<AccountTreeIcon />}
                  endIcon={<UnfoldIcon />}
                  sx={{ justifyContent: "flex-start", mb: 2.5, minHeight: 44, "& .MuiButton-endIcon": { ml: "auto" } }}
                >
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {pathLabel(index, node) || "Choisir dans le catalogue"}
                  </Box>
                </Button>
                <Drawer
                  anchor="bottom"
                  open={treeOpen}
                  onClose={() => setTreeOpen(false)}
                  slotProps={{ paper: { sx: { height: "78vh", borderTopLeftRadius: 16, borderTopRightRadius: 16 } } }}
                >
                  <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: tokens.borderStrong, mx: "auto", mt: 1 }} />
                  <Typography variant="subtitle1" sx={{ px: 2, pt: 1 }}>
                    Catalogue
                  </Typography>
                  {tree}
                </Drawer>
                {detail}
              </>
            ) : (
              <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: `${TREE_WIDTH}px minmax(0, 1fr)`, alignItems: "start" }}>
                <Paper
                  variant="outlined"
                  component="aside"
                  sx={{
                    borderRadius: 3,
                    position: "sticky",
                    top: 24,
                    maxHeight: "calc(100vh - 48px)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  {tree}
                </Paper>
                <Box sx={{ minWidth: 0 }}>{detail}</Box>
              </Box>
            )}
          </>
        )}

        <ProgramDialog
          open={creating}
          onClose={() => setCreating(false)}
          program={null}
          onCreated={(id) => select({ kind: "program", id })}
        />
      </Box>
    </CatalogProvider>
  );
};

export default CatalogPage;
